// @ts-check
/* eslint-env browser */

import { reactiveStore } from '../../reactive-store.js'

/**
 * Web component for handling "Delete tree" / "Delete zone" button clicks without page reload.
 *
 * Usage:
 *   <delete-feature entity-id="123" feature-type="tree">
 *     <button>Delete tree</button>
 *   </delete-feature>
 */
export class DeleteFeature extends HTMLElement {
  connectedCallback () {
    const button = this.querySelector('button')
    if (!button) {
      console.warn('delete-feature: No button found inside component')
      return
    }

    button.addEventListener('click', (evt) => {
      evt.preventDefault()

      const entityId = this.getAttribute('entity-id')
      if (!entityId) {
        console.error('delete-feature: No entity-id attribute found')
        return
      }

      // Set up dialog content
      const featureType = this.getAttribute('feature-type') || 'item'
      const entityName = featureType === 'tree' ? 'tree' : featureType === 'tree_preservation_zone' ? 'zone' : 'area'
      const title = `Delete ${entityName}`
      const message = `Are you sure you want to delete this ${entityName}?`

      // Create confirmation dialog
      const dialogRoot = document.getElementById('dialog-root') || document.body
      const wrapper = document.createElement('div')
      wrapper.innerHTML = `
        <modal-dialog>
          <dialog class="modal-dialog">
            <h2 class="modal-dialog__title govuk-heading-m">${title}</h2>
            <p class="modal-dialog__message govuk-body">${message}</p>
            <div class="govuk-button-group">
              <button class="govuk-button govuk-button--warning" data-action="confirm">Delete ${entityName}</button>
              <button class="govuk-button govuk-button--secondary" data-action="cancel">Cancel</button>
            </div>
          </dialog>
        </modal-dialog>
      `
      const modalDialog = wrapper.firstElementChild
      dialogRoot.appendChild(modalDialog)

      // handle dialog confirmation
      modalDialog.addEventListener('confirm', () => {
        this.#performDeletion()
      }, { once: true })
    })
  }

  #performDeletion () {
    const entityId = this.getAttribute('entity-id')
    if (!entityId) {
      console.error('delete-feature: No entity-id attribute found')
      return
    }

    // Close the details element before deletion to prevent next item from expanding
    const details = this.closest('details')
    if (details instanceof HTMLDetailsElement) {
      details.removeAttribute('open')
    }

    // Focus parent tab when available (text-data is optional; deletion must still proceed)
    /** @type {import('../index.js').TextData | null} */
    const textData = this.closest('text-data')
    const panel = this.closest('.govuk-tabs__panel')
    if (textData && panel) {
      const panelId = panel.getAttribute('id')
      if (panelId) {
        const tab = textData.querySelector(`.govuk-tabs__tab[href="#${panelId}"]`)
        if (tab instanceof HTMLElement) {
          tab.focus()
        }
      }
    }

    reactiveStore.features.set(
      reactiveStore.features.get().filter(f => f.properties?._id !== entityId)
    )
    reactiveStore.selectedFeatureId.set('')
  }
}

customElements.define('delete-feature', DeleteFeature)
